import {z} from 'zod/v4';

/**
 * Accepts Date objects, ISO date strings, or ISO datetime strings and
 * always produces a Date instance.
 */
export const Dateish = z
  .union([z.date(), z.iso.date(), z.iso.datetime()])
  .transform((val) => (val instanceof Date ? val : new Date(val)));

/**
 * Validates that a string is a JSON-encoded non-empty array of strings.
 * The value remains a string (no transform) so it can be forwarded as-is.
 */
export const JsonStringArray = z.string().refine(
  (val) => {
    try {
      const parsed = JSON.parse(val);
      return (
        Array.isArray(parsed) &&
        parsed.length > 0 &&
        parsed.every((item) => typeof item === 'string')
      );
    } catch {
      return false;
    }
  },
  {message: 'Must be a JSON-encoded non-empty array of strings'}
);

/**
 * JSON-encoded array of platform IDs for prompt-insights filters. Blank
 * entries are dropped after parse; an empty array means no platform filter.
 */
export const PlatformIdsJsonArray = z
  .string()
  .refine(
    (val) => {
      try {
        const parsed = JSON.parse(val);
        return (
          Array.isArray(parsed) &&
          parsed.every((item) => typeof item === 'string')
        );
      } catch {
        return false;
      }
    },
    {message: 'Must be a JSON-encoded array of strings'}
  )
  .transform((val) =>
    (JSON.parse(val) as string[]).filter((id) => id.trim().length > 0)
  );

/**
 * Reusable summary with total and active counts,
 * used for aggregated child-entity summaries (e.g. topics on a brand, prompts on a topic).
 */
export const AeoAssetCountSummary = z.object({
  total: z.number(),
  active: z.number(),
});
export type AeoAssetCountSummary = z.infer<typeof AeoAssetCountSummary>;
